import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Shape réponse /auth/me backend (camelCase — Phase 4.A)
 */
export interface User {
  id: string;
  email: string;
  fullName: string;
  status: string;
  twofaEnabled: boolean;
  roles: string[];           // ex: ["CLIENT_POWER_USER"]
  permissions: string[];     // ex: ["analyse.voir", "querybuilder.use"]
  scopeIndustry: string;     // "PHONE" V1
  language?: 'fr' | 'en'; // `#796` i18n — langue resolue du compte
}

interface UserStore {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;

  setUser: (user: User) => void;
  setAccessToken: (token: string) => void;
  logout: () => void;

  /**
   * OR logic si array — au moins une permission suffit.
   * String seule = check direct.
   */
  hasPermission: (perm: string | string[]) => boolean;

  /**
   * AND logic — toutes les permissions requises.
   */
  hasAllPermissions: (perms: string[]) => boolean;
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      setUser: (user) => set({ user, isAuthenticated: true }),

      setAccessToken: (token) => set({ accessToken: token }),

      logout: () => set({ user: null, accessToken: null, isAuthenticated: false }),

      hasPermission: (perm) => {
        const { user } = get();
        if (!user) return false;
        if (Array.isArray(perm)) {
          return perm.some((p) => user.permissions.includes(p));
        }
        return user.permissions.includes(perm);
      },

      hasAllPermissions: (perms) => {
        const { user } = get();
        if (!user) return false;
        return perms.every((p) => user.permissions.includes(p));
      },
    }),
    {
      name: 'orbicore-user',
      partialize: (s) => ({
        user: s.user,
        accessToken: s.accessToken,
        isAuthenticated: s.isAuthenticated,
      }),
    }
  )
);
